import { DETAILED, SLUGS } from "@components/home/routes";
import MainWrapper from "@components/pages/common/MainWrapper";
import {
  RAQ_FORM_TYPES,
  RAQ_PAGE_TYPE,
  STORAGE_LIKES,
} from "@components/utils/constants";
import { handleResponseCode } from "@components/utils/getStaticPropsHelpers";
import DetailsPage from "@modules/detailsPage/DetailsPage";
import fetchGet from "@services/api/fetchGet";
import baseApiCalls from "@services/baseApiCalls";
import detectRedirect from "@services/detectRedirect";
import { GetStaticPaths, GetStaticProps } from "next";
import {
  IFooterResponse,
  IMediaAssetResponse,
  IMenuResponse,
  ISearchManagementResponse,
  ITopNavigationResponse,
} from "../../../types/commonResponses";
import { IUSPComponentResponse } from "../../../types/componentResponses";
import { PAGE_TYPES } from "../../../types/index";
import {
  IActivityResponse,
  ILocationResponse,
  IPackageResponse,
  IRegionResponse,
} from "../../../types/modelResponses";

type RaQFormTypeKeys = keyof typeof RAQ_FORM_TYPES;
export type RaQFormType = (typeof RAQ_FORM_TYPES)[RaQFormTypeKeys];

type RaQPageTypeKeys = keyof typeof RAQ_PAGE_TYPE;
export type RaQPageType = (typeof RAQ_PAGE_TYPE)[RaQPageTypeKeys];

interface IActivityDetailsPage {
  footer: IFooterResponse;
  packages: IPackageResponse;
  activity: IActivityResponse;
  mediaAssets: IMediaAssetResponse | null;
  topNavigation: ITopNavigationResponse;
  allLocations: ILocationResponse;
  region: IRegionResponse;
  pageType: RaQPageType;
  menus: IMenuResponse;
  usp: IUSPComponentResponse[];
  raqFormType: RaQFormType;
  searchManagement: ISearchManagementResponse;
}

function ActivityDetail(props: IActivityDetailsPage): JSX.Element {
  return (
    <MainWrapper>
      <DetailsPage
        data={props.activity}
        region={props.region}
        allPackages={props.packages}
        allLocations={props.allLocations}
        raqFormType={props.raqFormType}
        topNavigation={props.topNavigation}
        pageType={RAQ_PAGE_TYPE.ACTIVITY}
        likesType={STORAGE_LIKES.activities}
      />
    </MainWrapper>
  );
}

export default ActivityDetail;

export const getStaticPaths: GetStaticPaths = async () => {
  const res = await fetchGet(SLUGS.ACTIVITIES);
  const activities = await res.json();

  const paths = activities.map((e: Pick<IActivityResponse, "SEO">) => ({
    params: {
      location: e?.SEO?.Slug.split("/")[0],
      slug: e?.SEO?.Slug.split("/"),
    },
  }));

  return { paths, fallback: "blocking" };
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const redirect = await detectRedirect(
    `${params?.location}/things-to-do/${params?.slug?.[0]}`
  );
  if (redirect) {
    return redirect;
  }

  // Fixes bug with navigation (forwarding to 404 initially) - NAM-1959
  if (Array.isArray(params?.slug) && params?.slug[0] === "activity") {
    params.slug.splice(0, 1);
  }
  const activityData = await fetchGet(
    `${
      DETAILED.ACTIVITIES
    }${`${params?.location}@things-to-do@${params?.slug?.[0]}`}`
  );

  const { pageNotFound, hasInternalError } = handleResponseCode(activityData);

  if (pageNotFound) {
    return {
      notFound: true,
      revalidate: Number(process.env.REVALIDATE_TIMER),
    };
  }

  const activity = hasInternalError ? null : await activityData.json();
  const packages = hasInternalError ? null : activity?.packages;
  const allLocations = hasInternalError ? null : activity?.locations;

  //const raqFormData = await fetchGet(RAQ_FORM);
  //const raqForm = await raqFormData.json();

  const {
    footer,
    mediaAssets,
    searchManagementRes,
    topNavigation,
    menus,
    usp,
  } = await baseApiCalls();

  const pageType = hasInternalError
    ? PAGE_TYPES.InternalErrorPage
    : PAGE_TYPES.Activity;
  return {
    props: {
      footer,
      packages,
      mediaAssets,
      topNavigation,
      activity,
      allLocations,
      pageType,
      menus,
      usp,
      //raqFormType: raqForm?.Form_Type || RAQ_FORM_TYPES.FORM_A,
      raqFormType: RAQ_FORM_TYPES.FORM_B,
      searchManagement: searchManagementRes,
      hasInternalError,
    },
    revalidate: Number(process.env.REVALIDATE_TIMER),
  };
};
